import { LL } from 'i18n/i18n.service';
import type { TranslationFunctions } from '../../gensrc/i18n/i18n-types';
import { TranslatedString } from 'models/translated-string';

export interface MealNameSuggestion {
  // Either a translated default name (LL.meals.*()) or the user's own logged
  // meal name branded via untranslatedString() — both are TranslatedString.
  name: TranslatedString;
  time_hr: number;
}

/** What the name choice needs to know about the day the new meal goes on. */
export interface MealNameDay {
  /** is the meal being added on today's date? */
  isToday: boolean;
  /** the current local time in decimal hours; only read when `isToday` */
  now_hr: number;
  /** the stored names of the meals the day already holds */
  takenNames: string[];
  /** the local time in decimal hours of the day's last meal, 0 when the day holds none */
  lastMealTime_hr: number;
}

/**
 * Choose the name and time for a meal the user adds by hand.
 *
 * Kept free of the database and Angular so it can be unit-tested; `FoodDbService.suggestMealNameAsync`
 * does the reads and calls this.
 *
 * `suggestions` are the user's usual meal names with their usual times, sorted by time.
 *
 * The answer is never a name a meal on that day already holds (#6204). A day shows and totals
 * only one meal per name (`mealSlotKey` in shared/src/data-model/log-meal.ts), so a reused name
 * would hide the new meal and every food in it from the day's list and calorie total.
 */
export function pickMealNameSuggestion(
  suggestions: MealNameSuggestion[],
  day: MealNameDay,
  ll: TranslationFunctions = LL,
): MealNameSuggestion {
  const taken = new Set(day.takenNames);
  const freeSuggestions = suggestions.filter(s => !taken.has(s.name));

  // if adding a meal today, use the free name closest to now
  if (day.isToday) {
    let bestDistance = 2; // anything further than 2 hours is ignored
    let bestName: TranslatedString = undefined;
    for (let s of freeSuggestions) {
      let d = Math.abs(s.time_hr - day.now_hr);
      if (d < bestDistance) {
        bestName = s.name;
        bestDistance = d;
      }
    }

    return { name: bestName ?? freeSnackName(taken, ll), time_hr: day.now_hr };
  }

  // if adding on any other day, get the next free suggestion after the day's last meal
  let bestDistance = 100;
  let best: MealNameSuggestion = null;
  for (let s of freeSuggestions) {
    let d = s.time_hr - day.lastMealTime_hr;
    if (d > 0 && d < bestDistance) {
      best = s;
      bestDistance = d;
    }
  }
  if (best) {
    return best;
  }

  // if all else fails, return another snack, just one minute after the last meal
  return { name: freeSnackName(taken, ll), time_hr: day.lastMealTime_hr + 1 / 60 };
}

/** "Snack" if the day does not hold one yet, otherwise the first free "Snack 2", "Snack 3", ... */
function freeSnackName(taken: Set<string>, ll: TranslationFunctions): TranslatedString {
  let name = ll.meals.snack();
  for (let number = 2; taken.has(name); number++) {
    name = ll.meals.snackNumbered({ number });
  }
  return name;
}
